import {
  asHostname,
  bqnsNamedGiven,
  isBqnNamed,
  parseHostname,
  withBqnId,
} from "./api";

// The names of the BQNs of the cluster, as they were reported, so that a BQN
// is asked for its name once only: the name it had is kept while it is
// offline, and it is asked for it again when it comes online without one.
const _hostnames = {};

export const rememberNodes = (nodes) => {
  nodes.forEach(({ bqnId, hostname }) => {
    if (hostname !== undefined) {
      _hostnames[bqnId] = hostname;
    }
  });

  return nodes;
};

// A BQN ID is a number: an error line of the table is parsed as a row too,
// and the name it gives is nobody's.
const isBqnId = (bqnId) => /^\d+$/.test(bqnId);

// The names "show system hostname detail" reports, by BQN ID, are kept too:
// the cluster dashboard asks for them, so after it the selector needs to ask
// no BQN for its name. A BQN that reports no name of use, or a row that names
// no BQN, is left out of the names given back, so that no reader takes them
// as names either.
export const rememberHostnames = (hostnames) => {
  const hostnamesReported = Object.fromEntries(
    Object.entries(hostnames ?? {})
      .filter(([bqnId]) => isBqnId(bqnId))
      .map(([bqnId, hostname]) => [
        bqnId,
        hostname === undefined ? undefined : asHostname(hostname),
      ])
      .filter(([, hostname]) => hostname !== undefined),
  );

  Object.assign(_hostnames, hostnamesReported);

  return hostnamesReported;
};

// The name a BQN was given is no longer the one it reported, so the name kept
// is dropped and the BQN is asked for its name again: nothing else asks for
// one while a BQN has a name, so a BQN renamed would keep the name it had.
export const forgetNodeName = (bqnId) => {
  delete _hostnames[bqnId];
};

// Every name kept is dropped, so that the BQNs of the cluster are asked for
// theirs again.
export const forgetNodeNames = () => {
  Object.keys(_hostnames).forEach(forgetNodeName);
};

// The BQNs of "show cluster bqn" as the nodes of the GUI: the function the
// cluster reports tells the ones offline, and the name of a BQN is the one it
// reported the last time it was asked for it.
export const asNodes = (bqns) =>
  rememberNodes(
    (bqns ?? []).map((bqn) => ({
      ...bqn,
      offline: bqn.function === "offline",
      hostname: bqn.hostname ?? _hostnames[bqn.bqnId],
    })),
  );

// Each BQN is asked for its own name, which is what carries the "bqn-id ID"
// option: the names known already need no asking again. A BQN that answers an
// error, which is what a BQN listed as online but disconnected answers, is
// taken as offline.
//
// The request is given no timeout of its own: the CLI client disconnects the
// transport when a request expires, taking the whole GUI down with it, so the
// answer is waited for as long as the CLI takes to report the BQN that does
// not answer.
const nodeNamed = ({ bqnId }) =>
  ifCl
    .run(withBqnId("show system hostname", bqnId))
    .then(parseHostname)
    .catch(() => undefined)
    .then((hostname) => [bqnId, hostname]);

// Nothing is asked while every BQN of the cluster is named: only the BQN that
// is online without a name of its own is asked for it.
export const nodesNamed = (nodes) => {
  const nodesToName = (nodes ?? []).filter((bqn) => isBqnNamed(bqn) === false);

  return nodesToName.length === 0
    ? Promise.resolve(nodes)
    : Promise.all(nodesToName.map(nodeNamed)).then((named) =>
        rememberNodes(bqnsNamedGiven(nodes, Object.fromEntries(named))),
      );
};
